import { z } from 'zod'
import { McpScope } from './mcp-scopes.js'
import { HandoffPolicy } from './handoff.js'

/**
 * Host grants and OAuth surface — SM1 (B4, B8, B21.6, V17-CFG-04, MCP-AC-027/125/131).
 *
 * A host grant is the durable answer to "what may this host do with this account, and over which
 * items?" — it is created by an explicit consent, it is revocable at any time, and it is the thing
 * every account-scoped tool checks before it reads anything. Two rules are load-bearing:
 *
 * 1. **A grant can never broaden access by writing.** `workspace.write` does not let a host publish
 *    something into another host's view, and a grant change is recorded as an event, not a silent
 *    edit (MCP-AC-131).
 * 2. **Revocation is immediate and local.** A revoked grant stops working on the next call, and an
 *    open handoff belonging to it is cancelled rather than resumed (C5).
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()

/** Which host/distribution this grant belongs to; the server selects the adapter from this. */
export const HostIdentity = z
  .object({
    /** Registered client identifier from the host's distribution (never a self-declared name). */
    client_id: z.string().min(1).max(200),
    client_name: z.string().min(1).max(200),
    distribution: z.enum(['openai', 'claude', 'other']),
    redirect_uri: z.string().min(1).max(2048),
  })
  .strict()
export type HostIdentity = z.infer<typeof HostIdentity>

export const HostGrantState = z.enum(['PENDING_CONSENT', 'ACTIVE', 'REVOKED', 'EXPIRED'])
export type HostGrantState = z.infer<typeof HostGrantState>

/**
 * The C5 handoff policy every host grant carries (B8, C5). It is declared once here so a grant can
 * never be created with a weaker policy than the contract: revocation cancels an open handoff,
 * device inventory is never revealed to a host, and content returns to a host only with a separate
 * authorization.
 */
export const HOST_GRANT_HANDOFF_POLICY: HandoffPolicy = {
  policy_version: 1,
  url_carries_operation_id_only: true,
  device_inventory_revealed_to_mcp: false,
  content_return_requires_separate_authorization: true,
  completion_reported_only_after_commit: true,
  duplicate_open: 'IDEMPOTENT_SINGLE_OPERATION',
  wrong_device: 'EXPLAIN_TRUTH',
  expired_session: 'EXPLAIN_TRUTH_WITH_RESTART',
  host_revocation: 'CANCEL_AND_REPORT',
}

/**
 * The refinement-free shape. `.pick()` cannot run on a schema with refinements (zod 4), and
 * `ConnectionActivity` needs a projection of exactly this object.
 */
const HostGrantObject = z
  .object({
    grant_id: Id,
    account_user_id: Id,
    host: HostIdentity,
    state: HostGrantState,
    scopes: z.array(McpScope).min(1),
    /** Items the grant may see. Empty means "everything the account published to this host". */
    item_scope: z
      .object({
        mode: z.enum(['ALL_PUBLISHED', 'SELECTED_ITEMS']),
        item_ids: z.array(Id).max(500),
      })
      .strict(),
    policy: HandoffPolicy,
    granted_at: IsoDateTime,
    /** Set when the state changed away from ACTIVE; the reason is never content. */
    state_changed_at: IsoDateTime.optional(),
    last_used_at: IsoDateTime.optional(),
  })
  .strict()

export const HostGrant = HostGrantObject.superRefine((grant, ctx) => {
  if (grant.item_scope.mode === 'SELECTED_ITEMS' && grant.item_scope.item_ids.length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'a selected-items grant names at least one item',
      path: ['item_scope', 'item_ids'],
    })
  }
  if (grant.state !== 'ACTIVE' && grant.state_changed_at === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: 'a non-active grant records when it changed',
      path: ['state_changed_at'],
    })
  }
})
export type HostGrant = z.infer<typeof HostGrant>

/**
 * Consent is the only way a grant becomes ACTIVE. It records the exact scopes and item scope the
 * person saw, so a later scope change requires a new consent rather than a silent widening.
 */
export const HostConsentRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    host: HostIdentity,
    scopes: z.array(McpScope).min(1),
    item_scope_mode: z.enum(['ALL_PUBLISHED', 'SELECTED_ITEMS']),
    item_ids: z.array(Id).max(500).default([]),
  })
  .strict()
  .superRefine((request, ctx) => {
    if (request.item_scope_mode === 'SELECTED_ITEMS' && request.item_ids.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'selected-items consent names at least one item',
        path: ['item_ids'],
      })
    }
  })
export type HostConsentRequest = z.infer<typeof HostConsentRequest>

export const HostConsentResponse = z
  .object({
    grant: HostGrant,
    /** Echoed so the consent screen and the stored grant can be compared by a reviewer. */
    consented_scopes: z.array(McpScope),
  })
  .strict()
export type HostConsentResponse = z.infer<typeof HostConsentResponse>

export const HostGrantChangeRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    reason: z.string().max(200).optional(),
  })
  .strict()
export type HostGrantChangeRequest = z.infer<typeof HostGrantChangeRequest>

/**
 * What the authorization endpoint answers when the person's consent is required before a code can
 * be issued: exactly the consent to render, and whether it widens an existing grant. The trusted
 * surface records the consent through `POST /v1/mcp/grants`; the authorization request itself never
 * creates or widens a grant.
 */
export const McpConsentChallenge = z
  .object({
    consent_required: z.literal(true),
    issuer: z.string().min(1).max(2048),
    client: HostIdentity,
    /** The scopes and item scope the person is being asked to approve. */
    consent: HostConsentRequest,
    /** True when this is an incremental elevation on top of an existing grant. */
    elevation: z.boolean(),
    /** The grant state before this request, so the surface can explain what changes. */
    current_state: HostGrantState.nullable(),
  })
  .strict()
export type McpConsentChallenge = z.infer<typeof McpConsentChallenge>

/**
 * A scope change is a new consent for the added scopes; a reduction takes effect immediately.
 * This shape exists so the API cannot accept a widening without a consent record.
 */
export function scopeChangeRequiresConsent(input: {
  readonly granted: readonly z.infer<typeof McpScope>[]
  readonly requested: readonly z.infer<typeof McpScope>[]
}): boolean {
  return input.requested.some((scope) => !input.granted.includes(scope))
}

/**
 * Whether a host may write into the workspace: a publish always requires the person's explicit
 * approval, whatever scopes the grant carries (MCP-AC-131).
 */
export function publishRequiresApproval(input: {
  readonly scopes: readonly z.infer<typeof McpScope>[]
}): boolean {
  void input
  return true
}

/** What the account sees about its connections (SM2's `signbento_get_connection_activity`). */
export const ConnectionActivity = z
  .object({
    grants: z.array(
      HostGrantObject.pick({
        grant_id: true,
        host: true,
        state: true,
        scopes: true,
        granted_at: true,
      }),
    ),
    /** Tool calls per grant over the reporting window; counts only, never content. */
    usage: z.array(
      z
        .object({
          grant_id: Id,
          calls: z.number().int().nonnegative(),
          last_call_at: IsoDateTime.optional(),
        })
        .strict(),
    ),
  })
  .strict()
export type ConnectionActivity = z.infer<typeof ConnectionActivity>

/* ------------------------------ OAuth surface ----------------------------- */

/**
 * Authorization-server metadata (RFC 8414 shape) for the MCP surface. `issuer` and `resource` are
 * release configuration, not literals: the register's `mcp_endpoint` row is the resource and the
 * issuer is frozen with it before coding (V17-CFG-04).
 */
export const OAuthServerMetadata = z
  .object({
    issuer: z.string().min(1).max(2048),
    authorization_endpoint: z.string().min(1).max(2048),
    token_endpoint: z.string().min(1).max(2048),
    registration_endpoint: z.string().min(1).max(2048),
    revocation_endpoint: z.string().min(1).max(2048),
    scopes_supported: z.array(McpScope).min(1),
    response_types_supported: z.array(z.literal('code')).min(1),
    grant_types_supported: z.array(z.enum(['authorization_code', 'refresh_token'])).min(1),
    code_challenge_methods_supported: z.array(z.literal('S256')).min(1),
    /** Clients are public (CIMD metadata documents or registered, no secret). */
    registration_endpoint_auth_methods_supported: z.array(z.enum(['none'])).min(1),
    token_endpoint_auth_methods_supported: z
      .array(z.enum(['none']))
      .min(1)
      .optional(),
    /**
     * RFC 9207: the authorization response carries `iss`, and clients validate it. Advertised so a
     * host can rely on the mixing-attack defence instead of assuming it.
     */
    authorization_response_iss_parameter_supported: z.literal(true).optional(),
    /** CIMD (SEP-991/B8.1) is the primary registration mechanism. */
    client_id_metadata_document_supported: z.literal(true).optional(),
    /** RFC 8707 resource indicators: the requested audience is bound into the token. */
    resource_parameter_supported: z.literal(true).optional(),
    /** RFC 9728 protected-resource metadata for the MCP resource this server authorizes. */
    protected_resource_metadata_endpoint: z.string().min(1).max(2048).optional(),
  })
  .strict()
export type OAuthServerMetadata = z.infer<typeof OAuthServerMetadata>

/**
 * RFC 9728 protected-resource metadata. `resource` is the MCP endpoint from release configuration
 * (V17-CFG-04), never a literal, and `authorization_servers` names the issuer that may mint tokens
 * for it.
 */
export const OAuthProtectedResourceMetadata = z
  .object({
    resource: z.string().min(1).max(2048),
    authorization_servers: z.array(z.string().min(1).max(2048)).min(1),
    scopes_supported: z.array(McpScope).min(1),
    /** A bearer token travels in the `Authorization` header; never a query parameter. */
    bearer_methods_supported: z.array(z.literal('header')).min(1),
    resource_name: z.string().min(1).max(200).optional(),
    resource_documentation: z.string().min(1).max(2048).optional(),
  })
  .strict()
export type OAuthProtectedResourceMetadata = z.infer<typeof OAuthProtectedResourceMetadata>

/**
 * Client metadata — the body of a Client ID Metadata Document (CIMD) and of a Dynamic Client
 * Registration request. `application_type` is required (SEP-837): an authorization server cannot
 * apply the right redirect rules without knowing whether the client is a web or a native app.
 */
export const APPLICATION_TYPES = ['web', 'native'] as const
export const OAuthApplicationType = z.enum(APPLICATION_TYPES)
export type OAuthApplicationType = z.infer<typeof OAuthApplicationType>

export const OAuthClientMetadata = z
  .object({
    /** For CIMD this is the HTTPS URL of the document itself; for DCR the issued identifier. */
    client_id: z.string().min(1).max(2048),
    client_name: z.string().min(1).max(200),
    redirect_uris: z.array(z.string().min(1).max(2048)).min(1).max(10),
    application_type: OAuthApplicationType,
    token_endpoint_auth_method: z.literal('none'),
    grant_types: z
      .array(z.enum(['authorization_code', 'refresh_token']))
      .min(1)
      .optional(),
    response_types: z.array(z.literal('code')).min(1).optional(),
    scope: z.string().max(2048).optional(),
  })
  .strict()
export type OAuthClientMetadata = z.infer<typeof OAuthClientMetadata>

/**
 * A dynamic client registration request (RFC 7591): the same metadata without `client_id`, which
 * the server issues. Only valid while the deprecation window is open — CIMD is the primary
 * mechanism, and this schema exists so the compatibility path stays explicit.
 */
export const OAuthClientRegistrationRequest = OAuthClientMetadata.omit({ client_id: true })
export type OAuthClientRegistrationRequest = z.infer<typeof OAuthClientRegistrationRequest>

/** OAuth 2.0 error codes this surface can return (RFC 6749 §5.2, RFC 8707, RFC 6750). */
export const OAUTH_ERROR_CODES = [
  'invalid_request',
  'unsupported_response_type',
  'invalid_client',
  'invalid_grant',
  'unauthorized_client',
  'unsupported_grant_type',
  'invalid_scope',
  'invalid_target',
  'access_denied',
  'insufficient_scope',
  'server_error',
] as const
export const OAuthErrorCode = z.enum(OAUTH_ERROR_CODES)
export type OAuthErrorCode = z.infer<typeof OAuthErrorCode>

export const OAuthErrorResponse = z
  .object({ error: OAuthErrorCode, error_description: z.string().max(400).optional() })
  .strict()
export type OAuthErrorResponse = z.infer<typeof OAuthErrorResponse>

/**
 * Per-tool authorization advertisement (B8.2, MCP-AC-084). A host must be able to tell an
 * anonymous discovery tool from a protected one without probing it, so every `tools/list` entry
 * carries the scheme it needs — an empty list for a no-auth tool, one `oauth2` scheme naming the
 * exact scope otherwise.
 */
export const McpSecurityScheme = z
  .object({ type: z.literal('oauth2'), scopes: z.array(McpScope).min(1) })
  .strict()
export type McpSecurityScheme = z.infer<typeof McpSecurityScheme>

export function securitySchemesForScope(scope: McpScope | null): readonly McpSecurityScheme[] {
  return scope === null ? [] : [{ type: 'oauth2', scopes: [scope] }]
}

/** PKCE is mandatory: a public client without S256 is refused, never downgraded. */
export function pkceAccepted(input: {
  readonly code_challenge?: string
  readonly code_challenge_method?: string
}): boolean {
  return (
    input.code_challenge !== undefined &&
    input.code_challenge.length >= 43 &&
    input.code_challenge_method === 'S256'
  )
}

export const GRANT_RULES = {
  consent_required_for_new_scopes: true,
  revocation_takes_effect_immediately: true,
  revoked_grant_cancels_open_handoff: true,
  publish_always_requires_approval: true,
  workspace_write_can_broaden_host_access: false,
  client_declared_host_name_is_trusted: false,
  /** CIMD is the primary registration mechanism; DCR only runs while its deprecation window is open. */
  cimd_is_primary_registration: true,
  dcr_only_during_deprecation_window: true,
  /** Every protected call re-reads the grant's current authorization revision from D1. */
  stale_token_never_bypasses_revocation: true,
  /** A scope never implies object access: the selected-item scope is a conjunctive requirement. */
  scope_never_implies_object_access: true,
  /** The account comes from the validated session/token, never from a host-asserted email. */
  host_asserted_email_is_never_authentication: true,
} as const
